/**
 * Dispatchers Bids — every bid email a dispatcher sent a broker, newest first.
 *
 * One row per SEND, not per load: a desk that quoted $700 and came back at $820 is two rows, because the
 * second ask is the interesting one and a per-load view would hide it.
 *
 * Every column is the EVENT, recorded as the email went out — the rate and the sender, and the lane, the
 * broker and the notes as the dispatcher was reading them at that moment. Nothing here is the load as the
 * board holds it NOW: a re-posted load must not re-write the context of last month's bid, and holding
 * these fields on the bid row is also what keeps the search affordable.
 *
 * The window, search, paging and empty states are `BidListPage`'s — shared with Drivers Bids, which is
 * the same read against the other side of the desk.
 *
 * Backend: GET /v1/gluon/dispatcher-bids (any control-panel operator). Read-only.
 */
import Chip from '@mui/joy/Chip'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import { formatUsdWhole } from '@numeo/types-core'
import type { DispatcherBidRow } from '@numeo/types'
import { BidListPage } from '@/components/bid-list/BidListPage'
import { Notes, place, when } from '@/components/bid-list/cells'
import { fetchDispatcherBids } from './api'

const Row = ({ bid }: { bid: DispatcherBidRow }) => (
    <tr key={bid.id}>
        <td>
            <Typography level="body-sm">{place(bid.originCity, bid.originState)}</Typography>
            <Typography level="body-xs" textColor="text.tertiary">
                → {place(bid.destinationCity, bid.destinationState)}
                {bid.miles ? ` · ${Math.round(bid.miles).toLocaleString('en-US')} mi` : ''}
            </Typography>
        </td>
        <td>
            <Typography level="body-sm">{bid.dispatcherName || '—'}</Typography>
            <Typography level="body-xs" textColor="text.tertiary">
                {bid.senderEmail || '—'}
            </Typography>
        </td>
        <td>
            {/* A bid with no driver is the normal Load Hub case and the unpicked Box Truck one — an em
                dash says "this ask was not aimed at a unit", which is a different statement from a blank
                cell. An id with no name is a driver the operator never named; showing the id would be
                noise, so the cell says there is one without pretending to know who. */}
            <Typography level="body-sm">{bid.driverName || (bid.driverId ? 'Unnamed driver' : '—')}</Typography>
        </td>
        <td>
            <Typography level="body-sm">{bid.companyName || bid.projectName}</Typography>
            {bid.companyName && (
                <Typography level="body-xs" textColor="text.tertiary">
                    {bid.projectName}
                </Typography>
            )}
        </td>
        <td>
            <Typography level="body-sm">{bid.brokerName || '—'}</Typography>
            {bid.postedRate !== undefined && (
                <Typography level="body-xs" textColor="text.tertiary">
                    posted {formatUsdWhole(bid.postedRate)}
                </Typography>
            )}
        </td>
        <td>
            <Typography level="body-sm" fontWeight="lg">
                {formatUsdWhole(bid.bidRate)}
            </Typography>
            {/* Flagged, not hidden. The rate is the calculator's; a hand-edited body means it is not
                provably the figure the broker read, and an average that silently mixes the two is a claim
                nobody can stand behind. */}
            {bid.bodyEdited && (
                <Tooltip title="The email body was edited by hand, so this is the calculator's figure rather than provably the one sent.">
                    <Chip size="sm" variant="soft" color="warning">
                        edited
                    </Chip>
                </Tooltip>
            )}
        </td>
        <td>
            <Notes text={bid.loadNotes} />
        </td>
        <td>
            <Typography level="body-xs" textColor="text.tertiary">
                {when(bid.sentAt)}
            </Typography>
        </td>
    </tr>
)

export default function DispatcherBidsPageView() {
    return (
        <BidListPage<DispatcherBidRow>
            icon={<RequestQuoteIcon />}
            title="Dispatchers Bids"
            subtitle="Every bid email a dispatcher sent, with the rate they asked for. One row per send, so a re-bid appears twice."
            searchPlaceholder="Broker, city, sender email or driver"
            emptyText="No bids in this window."
            fetchPage={fetchDispatcherBids}
            head={
                <>
                    <th style={{ width: 220 }}>Lane</th>
                    <th style={{ width: 200 }}>Dispatcher</th>
                    <th style={{ width: 160 }}>Driver</th>
                    <th style={{ width: 180 }}>Company</th>
                    <th style={{ width: 180 }}>Broker</th>
                    <th style={{ width: 120 }}>Bid</th>
                    <th>Load notes</th>
                    <th style={{ width: 170 }}>Sent</th>
                </>
            }
            renderRow={(bid) => <Row key={bid.id} bid={bid} />}
        />
    )
}
